/**
 * Company Form Options
 * Shared constants for legal forms and industry sectors
 * Used in: Settings, Onboarding (Step2Details, Step3Confirm)
 */

export interface SelectOption {
  value: string;
  label: string;
}

/** Legal form value keys - use with useLegalFormOptions hook for translated labels */
export const LEGAL_FORM_KEYS = [
  "",
  "sp_z_oo",
  "sa",
  "sc",
  "jdg",
  "sp_jawna",
  "sp_komandytowa",
  "sp_partnerska",
  "fundacja",
  "stowarzyszenie",
  "spoldzielnia",
  "other",
] as const;


export const INDUSTRY_SECTOR_CODES = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "OTHER",
] as const;

export type IndustrySectorCode = (typeof INDUSTRY_SECTOR_CODES)[number];

export const INDUSTRY_SECTOR_OTHER_CODE: IndustrySectorCode = "OTHER";

export interface IndustrySectorOption {
  value: IndustrySectorCode;
  labelKey: string;
}

export const INDUSTRY_SECTOR_OPTIONS: IndustrySectorOption[] =
  INDUSTRY_SECTOR_CODES.map((value) => ({
    value,
    labelKey: `onboarding:step2.industrySector.options.${value}`,
  }));

const INDUSTRY_SECTOR_CODE_SET = new Set<string>(INDUSTRY_SECTOR_CODES);

export function isIndustrySectorCode(
  value: string,
): value is IndustrySectorCode {
  return INDUSTRY_SECTOR_CODE_SET.has(value);
}

/**
 * Resolve a company's onboarding `industry_sector` (a single NACE section
 * code or a multi-select array, `CompanyData["industry_sector"]`) to one
 * NACE section code for seeding the per-audit context form's default
 * (pirx-30g6l). Skips the free-text "OTHER" sentinel and any code the
 * matcher doesn't recognize; returns "" when nothing resolves, so the audit
 * form falls back to manual per-run selection.
 */
export function resolvePrimaryIndustrySector(
  selection: string[] | string | null | undefined,
): string {
  const candidates = Array.isArray(selection)
    ? selection
    : selection
    ? [selection]
    : [];
  return candidates.find(
    (code) =>
      isIndustrySectorCode(code) && code !== INDUSTRY_SECTOR_OTHER_CODE,
  ) ?? "";
}

/**
 * pirx-waua: declared ecolabel / certificate scheme options. Mirrored from
 * `backend/src/services/greenwashing/qualifying-ecolabels.ts` (the canonical
 * list). Keep this in sync with the backend list whenever a scheme is added
 * or reclassified — the backend `qualifyingSchemeNames()` is the source of
 * truth, this constant is a UX mirror for the dropdown.
 *
 * The 'other' entry is the free-text fallback (mirror of the
 * `legal_form` / `industry_sector` 'other' pattern); when picked the UI
 * reveals a `declared_certificate_other` text input.
 */
export const DECLARED_CERTIFICATE_OTHER_CODE = "other" as const;

export interface DeclaredCertificateOption {
  value: string;
  labelKey: string;
}

export const DECLARED_CERTIFICATE_OPTIONS: readonly DeclaredCertificateOption[] = [
  { value: "EU Ecolabel", labelKey: "onboarding:step2.declaredCertificate.options.EU Ecolabel" },
  { value: "Blue Angel", labelKey: "onboarding:step2.declaredCertificate.options.Blue Angel" },
  { value: "Nordic Swan", labelKey: "onboarding:step2.declaredCertificate.options.Nordic Swan" },
  { value: "Polish \"EKO\" ecolabel", labelKey: "onboarding:step2.declaredCertificate.options.Polish EKO ecolabel" },
  { value: "Austrian Ecolabel (Umweltzeichen)", labelKey: "onboarding:step2.declaredCertificate.options.Austrian Ecolabel Umweltzeichen" },
  { value: "Milieukeur", labelKey: "onboarding:step2.declaredCertificate.options.Milieukeur" },
  { value: "NF Environnement", labelKey: "onboarding:step2.declaredCertificate.options.NF Environnement" },
  { value: "EU Energy Label (class A)", labelKey: "onboarding:step2.declaredCertificate.options.EU Energy Label class A" },
  { value: DECLARED_CERTIFICATE_OTHER_CODE, labelKey: "onboarding:step2.declaredCertificate.options.other" },
];

// Case-insensitive lookup of the qualifying-scheme option values (excludes the
// free-text "other" sentinel), so a stored certificate that matches a known
// scheme resolves back to its canonical option value regardless of casing.
const DECLARED_CERTIFICATE_CANONICAL_BY_KEY = new Map<string, string>(
  DECLARED_CERTIFICATE_OPTIONS
    .filter((option) => option.value !== DECLARED_CERTIFICATE_OTHER_CODE)
    .map((option) => [option.value.toLowerCase(), option.value]),
);

/**
 * Resolve a stored certificate name to its canonical option value when it
 * matches a known qualifying scheme (case-insensitively); returns `null` for
 * free-text / unrecognized values, which the UI routes to the "other" entry.
 */
export function canonicalDeclaredCertificate(value: string): string | null {
  return DECLARED_CERTIFICATE_CANONICAL_BY_KEY.get(value.trim().toLowerCase()) ??
    null;
}

/** kebab-case `data-test` slug for a declared-certificate option value. */
export function declaredCertificateTestSlug(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}
